import OrchestratorAPI from './common.js';
import {
  type CreateValues,
  type Credential,
  type UpdateValues,
} from '../models/orchestrator/credential.js';

export default class API extends OrchestratorAPI {
  protected readonly API_PATH = '/credentials';
  public readonly baseEndpoint = `${this.BASE_URL}${this.API_PATH}`;

  async findOneById(id: string): Promise<Credential> {
    const url = this.constructPath(`/${id}`);
    const { credential } = await this.common.getJSON(url);
    return {
      ...credential,
      created: new Date(credential.created),
      updated: new Date(credential.updated),
    };
  }

  async getPassword(credentialId: string): Promise<string> {
    const url = this.constructPath(`/${credentialId}/password`);
    const { password } = await this.common.getJSON(url);
    return password;
  }

  async updatePassword(
    credentialId: string,
    args: {
      newPassword: string;
      newPasswordConfirmation: string;
    }
  ): Promise<void> {
    const url = this.constructPath(`/${credentialId}/password`);
    return await this.common.patchJSON(url, args);
  }

  async getTwoFactorCode(credentialId: string): Promise<{ code: string }> {
    const url = this.constructPath(`/${credentialId}/two-factor-code`);
    return await this.common.getJSON(url);
  }

  async getTwoFactorSecret(credentialId: string): Promise<{ twoFactorAuthSecret: string }> {
    const url = this.constructPath(`/${credentialId}/two-factor-secret`);
    return await this.common.getJSON(url);
  }

  async updateTwoFactorSecret(
    credentialId: string,
    args: {
      oldSecret: string | null;
      newSecret: string;
      newSecretConfirmation: string;
    }
  ): Promise<void> {
    const url = this.constructPath(`/${credentialId}/two-factor-code`);
    return await this.common.patchJSON(url, args);
  }

  async findAllForEhrInstance(ehrInstanceId: string): Promise<Credential[]> {
    const url = this.constructPath('/', { ehrInstanceId });
    const { credentials } = await this.common.getJSON(url);
    return credentials;
  }

  async create(values: CreateValues): Promise<Credential> {
    const url = this.constructPath('/');
    const { credential } = await this.common.postJSON(url, values);
    return this.deserialize(credential);
  }

  async update(id: string, values: UpdateValues): Promise<Credential> {
    const url = this.constructPath(`/${id}`);
    const { credential } = await this.common.patchJSON(url, values);
    return this.deserialize(credential);
  }

  async remove(id: string): Promise<void> {
    const url = this.constructPath(`/${id}`);
    return await this.common.deleteJSON(url);
  }
}
